import React, { useState, useEffect } from "react";
import {
  getInternalContacts,
  getExternalContacts,
  createExternalCompany,
  createExternalContact,
  createStandaloneContact,
  updateExternalCompany,
  updateExternalContact,
  updateInternalContact,
  deleteExternalCompany,
  deleteExternalContact,
  uploadClientCompanyLogo,
} from "../../services/directoryService";
import { useCurrentUser } from "../../context/UserContext";
import { usePermissions } from "../../hooks/usePermission";
import FormModal from "../../components/forms/FormModal";
import ui from "./DashboardUI.module.css";
import styles from "./DirectoryUI.module.css";
import useMinimumLoader from "../../hooks/useMinimumLoader";

export default function Directory() {
  const currentUser = useCurrentUser();
  const { 
    canViewInternalDirectory, 
    canViewExternalDirectory,
    canCreateExternalContacts,
    canEditExternalContacts,
    canEditPeople
  } = usePermissions();
  
  const [activeTab, setActiveTab] = useState(
    canViewExternalDirectory ? "external" : "internal"
  );
  const [internalContacts, setInternalContacts] = useState([]);
  const [externalCompanies, setExternalCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [, setToast] = useState(null);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddStandaloneContact, setShowAddStandaloneContact] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [standaloneContactsExpanded, setStandaloneContactsExpanded] = useState(true);

  const [companyForm, setCompanyForm] = useState({
    company_name: "",
    industry: "",
    website: "",
    billing_address: "",
    logo_url: "",
    company_notes: "",
    // Optional initial contact
    name: "",
    email: "",
    phone: "",
    job_title: "",
    department: "",
    is_primary: true,
    contact_notes: "",
  });

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    job_title: "",
    department: "",
    is_primary: false,
    notes: "",
  });

  const [standaloneContactForm, setStandaloneContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    job_title: "",
    department: "",
    notes: "",
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    // Clear selection when switching tabs
    setSelectedContact(null);
    setSelectedCompany(null);
  }, [activeTab]);

  const loadContacts = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      const promises = [];
      if (canViewInternalDirectory) {
        promises.push(getInternalContacts());
      }
      if (canViewExternalDirectory) {
        promises.push(getExternalContacts());
      }
      
      const results = await Promise.all(promises);
      
      let internalIdx = 0;
      let externalIdx = canViewInternalDirectory ? 1 : 0;
      
      if (canViewInternalDirectory) {
        setInternalContacts(results[internalIdx]?.contacts || []);
      }
      if (canViewExternalDirectory) {
        const companiesData = results[externalIdx]?.companies || [];
        // Sort companies alphabetically by company name, with standalone contacts (null company_name) first
        const sortedCompanies = companiesData.sort((a, b) => {
          const aName = a.company_name || '';
          const bName = b.company_name || '';
          return aName.localeCompare(bName);
        });
        setExternalCompanies(sortedCompanies);
      }
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to load contacts", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();

    if (!companyForm.company_name || companyForm.company_name.trim() === "") {
      showToast("Please enter a company name.", "error");
      return;
    }

    try {
      const result = await createExternalCompany(companyForm);
      
      // Upload logo if provided
      if (logoFile && result.client_company_id) {
        await handleLogoUpload(result.client_company_id, logoFile);
      }
      
      await loadContacts();

      setCompanyForm({
        company_name: "",
        industry: "",
        website: "",
        billing_address: "",
        logo_url: "",
        company_notes: "",
        name: "",
        email: "",
        phone: "",
        job_title: "",
        department: "",
        is_primary: true,
        contact_notes: "",
      });
      setLogoFile(null);

      setShowAddCompany(false);
      showToast("Company added successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to add company.", "error");
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();

    try {
      await createExternalContact(selectedCompanyId, contactForm);
      await loadContacts();

      setContactForm({
        name: "",
        email: "",
        phone: "",
        job_title: "",
        department: "",
        is_primary: false,
        notes: "",
      });

      setShowAddContact(false);
      setSelectedCompanyId(null);
      showToast("Contact added successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to add contact.", "error");
    }
  };

  const handleAddStandaloneContact = async (e) => {
    e.preventDefault();

    try {
      await createStandaloneContact(standaloneContactForm);
      await loadContacts();

      setStandaloneContactForm({
        name: "",
        email: "",
        phone: "",
        job_title: "",
        department: "",
        notes: "",
      });

      setShowAddStandaloneContact(false);
      showToast("Standalone contact added successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to add standalone contact.", "error");
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();

    try {
      await updateExternalCompany(editingCompany.client_company_id, editingCompany);
      
      // Upload new logo if provided
      if (logoFile) {
        await handleLogoUpload(editingCompany.client_company_id, logoFile);
      }
      
      await loadContacts();
      setEditingCompany(null);
      setLogoFile(null);
      showToast("Company updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update company.", "error");
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();

    try {
      await updateExternalContact(editingContact.client_contact_id, editingContact);
      await loadContacts();
      setEditingContact(null);
      showToast("Contact updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update contact.", "error");
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();

    try {
      // Only send the fields that can be updated for employees
      const employeeData = {
        name: editingContact.name,
        email: editingContact.email,
        phone_number: editingContact.phone,
        // Note: title_id and permissions are not included as per user requirements
      };
      
      await updateInternalContact(editingContact.user_id, employeeData);
      await loadContacts();
      setEditingContact(null);
      showToast("Employee updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update employee.", "error");
    }
  };

  const handleLogoUpload = async (clientCompanyId, file) => {
    try {
      const result = await uploadClientCompanyLogo(clientCompanyId, file);
      await loadContacts(); // Refresh to show new logo
      showToast("Logo uploaded successfully.", "success");
      return result.logo_url;
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to upload logo.", "error");
      throw err;
    }
  };

  const handleDeleteCompany = async () => {
    if (!window.confirm("Are you sure you want to delete this company and all its contacts?")) {
      return;
    }

    try {
      await deleteExternalCompany(editingCompany.client_company_id);
      await loadContacts();
      setEditingCompany(null);
      showToast("Company deleted successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to delete company.", "error");
    }
  };

  const handleDeleteContact = async () => {
    if (!window.confirm("Are you sure you want to delete this contact?")) {
      return;
    }

    try {
      await deleteExternalContact(editingContact.client_contact_id);
      await loadContacts();
      setEditingContact(null);
      showToast("Contact deleted successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to delete contact.", "error");
    }
  };

  const filterContacts = (contacts) => {
    if (!searchTerm) return contacts;
    const term = searchTerm.toLowerCase().trim();
    const filtered = contacts.filter(
      (c) => {
        const nameMatch = c.name && (() => {
          const nameParts = c.name.toLowerCase().split(' ');
          return nameParts.some(part => part.startsWith(term));
        })();
        const phoneMatch = c.phone && c.phone.toLowerCase().includes(term);
        const matches = nameMatch || phoneMatch;
        return matches;
      }
    );
    return filtered;
  };

  const filterCompanies = (companies) => {
    if (!searchTerm) {
      // Include all companies and standalone contacts
      return companies;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = companies.filter((company) => {
      const companyMatch = company.company_name?.toLowerCase().startsWith(term);

      const contactMatch = (company.contacts || [])?.some((contact) => {
        const nameMatch = contact.name && (() => {
          const nameParts = contact.name.toLowerCase().split(' ');
          return nameParts.some(part => part.startsWith(term));
        })();
        const phoneMatch = contact.phone && contact.phone.toLowerCase().includes(term);
        const matches = nameMatch || phoneMatch;
        return matches;
      });

      const matches = companyMatch || contactMatch;
      return matches;
    });
    return filtered;
  };

  const filteredInternal = filterContacts(internalContacts);
  const filteredExternal = filterCompanies(externalCompanies);

  const handleContactClick = (contact) => {
    setSelectedContact(contact);
    setSelectedCompany(null);
  };

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setSelectedContact(null);
    
    // Toggle expanded state for the company
    setExpandedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(company.client_company_id)) {
        newSet.delete(company.client_company_id);
      } else {
        newSet.add(company.client_company_id);
      }
      return newSet;
    });
  };

  return (
    <div className={styles.directoryRoot}>
      {showLoading ? (
        <div className={styles.directorySkeletonWrap}>
          <div className={styles.directorySkeletonHeader}>
            <div className={styles.directorySkeletonTitle} />
            <div className={styles.directorySkeletonSearch} />
            <div className={styles.directorySkeletonActions}>
              <div className={styles.directorySkeletonActionBtn} />
              <div className={styles.directorySkeletonActionBtn} />
            </div>
          </div>

          <div className={styles.directorySkeletonPanels}>
            <div className={styles.directorySkeletonLeftPanel}>
              <div className={styles.directorySkeletonTabs}>
                <div className={styles.directorySkeletonTab} />
                <div className={styles.directorySkeletonTab} />
              </div>
              <div className={styles.directorySkeletonList}>
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={`directory-list-item-${idx}`} className={styles.directorySkeletonListItem}>
                    <div className={styles.directorySkeletonAvatar} />
                    <div className={styles.directorySkeletonLine} />
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.directorySkeletonRightPanel}>
              <div className={styles.directorySkeletonDetailAvatar} />
              <div className={styles.directorySkeletonDetailTitle} />
              <div className={styles.directorySkeletonDetailLineLg} />
              <div className={styles.directorySkeletonDetailLine} />
              <div className={styles.directorySkeletonDetailLine} />
              <div className={styles.directorySkeletonDetailLineLg} />
            </div>
          </div>
        </div>
      ) : (
        <div className={`${ui.fadeInContent} ${styles.directoryPage}`}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Directory</h1>
        <div className={styles.searchBar}>
          <div className={styles.searchInputWrap}>
            <span className={styles.searchIcon} aria-hidden="true">🔍</span>
            <input
              type="text"
              placeholder="Search contacts, companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.actionButtons}>
            {canCreateExternalContacts && (
              <button className={`${styles.addButton} ${styles.secondary}`} onClick={() => setShowAddStandaloneContact(true)}>
                + Contact
              </button>
            )}
            {canCreateExternalContacts && (
              <button className={styles.addButton} onClick={() => setShowAddCompany(true)}>
                + Company
              </button>
            )}
          </div>
        </div>
      </div>
      <div className={styles.pageContainer}>
        <div className={styles.directoryLeftPanel}>
          {/* Tabs and Contact List */}
          <div className={styles.tabs}>
            {canViewExternalDirectory && (
              <button
                className={`${styles.tabButton} ${activeTab === "external" ? styles.active : ""}`}
                onClick={() => setActiveTab("external")}
              >
                External
              </button>
            )}
            {canViewInternalDirectory && (
              <button
                className={`${styles.tabButton} ${activeTab === "internal" ? styles.active : ""}`}
                onClick={() => setActiveTab("internal")}
              >
                Internal
              </button>
            )}
          </div>
          <div className={styles.directoryGrid}>
          {activeTab === "internal" &&
            filteredInternal.map((contact) => (
              <div
                key={contact.user_id}
                className={`${styles.contactCard} ${selectedContact?.user_id === contact.user_id ? styles.selected : ''}`}
                onClick={() => handleContactClick(contact)}
              >
                <div className={styles.contactAvatarContainer}>
                  {contact.avatar_url ? (
                    <img
                      src={contact.avatar_url}
                      alt={contact.name}
                      className={styles.contactListAvatar}
                    />
                  ) : (
                    <div className={styles.contactListAvatarFallback}>
                      {contact.name
                        .split(" ")
                        .map((word) => word[0])
                        .join("")}
                    </div>
                  )}
                </div>
                <span>{contact.name}</span>
              </div>
            ))}
          {activeTab === "external" && (
            <>
              {/* Company contacts */}
              {filteredExternal
                .filter((company) => company.client_company_id) // Only companies with IDs
                .map((company) => (
                  <div key={company.client_company_id} className={`${styles.companyCard} ${selectedCompany?.client_company_id === company.client_company_id ? styles.selected : ''}`}>
                    <div className={styles.companyHeader} onClick={() => handleCompanyClick(company)}>
                      <div className={styles.companyAvatarContainer}>
                        {company.logo_url ? (
                          <img
                            src={company.logo_url}
                            alt={`${company.company_name} logo`}
                            className={styles.companyListLogo}
                          />
                        ) : (
                          <div className={styles.companyAvatarFallback}>
                            {company.company_name.split(" ").map(word => word[0]).join("").slice(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className={styles.companyTitleWrap}>
                        <h3 className={styles.companyTitle}>{company.company_name}</h3>
                        <span className={styles.companySubtitle}>
                          {(company.contacts || []).length} {(company.contacts || []).length === 1 ? 'contact' : 'contacts'}
                        </span>
                      </div>
                      <div className={`${styles.expandIcon} ${expandedCompanies.has(company.client_company_id) ? styles.expanded : ''}`}>
                        ▼
                      </div>
                    </div>
                    {expandedCompanies.has(company.client_company_id) && (
                      <div className={styles.companyContacts}>
                        {(() => {
                          const companyMatches = company.company_name?.toLowerCase().startsWith(searchTerm.toLowerCase().trim());
                          return (company.contacts || []).filter(contact => {
                            if (companyMatches) return true; // Show all contacts if company matches
                            const nameMatch = contact.name && (() => {
                              const nameParts = contact.name.toLowerCase().split(' ');
                              return nameParts.some(part => part.startsWith(searchTerm.toLowerCase().trim()));
                            })();
                            const phoneMatch = contact.phone && contact.phone.toLowerCase().includes(searchTerm.toLowerCase().trim());
                            return nameMatch || phoneMatch;
                          });
                        })().map((contact) => (
                          <div
                            key={contact.client_contact_id}
                            className={`${styles.contactCard} ${selectedContact?.client_contact_id === contact.client_contact_id ? styles.selected : ''}`}
                            onClick={() => handleContactClick(contact)}
                          >
                            <span className={styles.contactCardAccent} />
                            {contact.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              
              {/* Standalone contacts grouped together */}
              {(() => {
                const standaloneContacts = filteredExternal
                  .filter((company) => !company.client_company_id) // Only standalone contacts
                  .flatMap((company) => company.contacts || [])
                  .filter(contact => {
                    const nameMatch = contact.name && (() => {
                      const nameParts = contact.name.toLowerCase().split(' ');
                      return nameParts.some(part => part.startsWith(searchTerm.toLowerCase().trim()));
                    })();
                    const phoneMatch = contact.phone && contact.phone.toLowerCase().includes(searchTerm.toLowerCase().trim());
                    return nameMatch || phoneMatch;
                  });
                
                if (standaloneContacts.length === 0) return null;
                
                return (
                  <div className={styles.companyCard}>
                    <div className={styles.companyHeader} onClick={() => setStandaloneContactsExpanded(!standaloneContactsExpanded)}>
                      <div className={styles.companyAvatarContainer}>
                        <div className={styles.companyAvatarFallback}>
                          SC
                        </div>
                      </div>
                      <div className={styles.companyTitleWrap}>
                        <h3 className={styles.companyTitle}>Standalone Contacts</h3>
                        <span className={styles.companySubtitle}>
                          {standaloneContacts.length} {standaloneContacts.length === 1 ? 'contact' : 'contacts'}
                        </span>
                      </div>
                      <div className={`${styles.expandIcon} ${standaloneContactsExpanded ? styles.expanded : ''}`}>
                        ▼
                      </div>
                    </div>
                    {standaloneContactsExpanded && (
                      <div className={styles.companyContacts}>
                        {standaloneContacts.map((contact) => (
                          <div
                            key={contact.client_contact_id}
                            className={`${styles.contactCard} ${selectedContact?.client_contact_id === contact.client_contact_id ? styles.selected : ''}`}
                            onClick={() => handleContactClick(contact)}
                          >
                            <span className={styles.contactCardAccent} />
                            {contact.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

        <div className={styles.directoryRightPanel}>
        {/* Company or Contact Details */}
        {selectedCompany ? (
          <div key={selectedCompany.client_company_id} className={styles.contactDetailsCard}>
            {/* Company Logo Row */}
            <div className={styles.companyLogoRow}>
              <div className={styles.contactAvatarContainer}>
                {selectedCompany.logo_url ? (
                  <img
                    src={selectedCompany.logo_url}
                    alt={`${selectedCompany.company_name} logo`}
                    className={styles.companyLogo}
                  />
                ) : (
                  <div className={styles.contactAvatarFallback}>
                    {selectedCompany.company_name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className={styles.companyNameStack}>
                <h2 className={styles.companyNameUnderLogo}>{selectedCompany.company_name}</h2>
                <span className={styles.companyNameMeta}>
                  {selectedCompany.industry || 'Client company'}
                  {(selectedCompany.contacts || []).length > 0 && ` · ${(selectedCompany.contacts || []).length} ${(selectedCompany.contacts || []).length === 1 ? 'contact' : 'contacts'}`}
                </span>
              </div>
              {canEditExternalContacts && (
                <div className={styles.companyActions}>
                  <button
                    type="button"
                    className={`${styles.addButton} ${styles.secondary}`}
                    onClick={() => setEditingCompany(selectedCompany)}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            {/* Company Details and Main Contact Row */}
            <div className={styles.companyDetailsLayout}>
              {/* Company Information */}
              <div className={styles.companyInfo}>
                <h3>Company Details</h3>
                <div className={styles.contactDetailItem}>
                  <span className={styles.contactLabel}>Industry</span>
                  <span className={`${styles.contactValue} ${!selectedCompany.industry ? styles.contactValueMuted : ''}`}>{selectedCompany.industry || 'Not set'}</span>
                </div>
                <div className={styles.contactDetailItem} style={{ marginTop: '0.75rem' }}>
                  <span className={styles.contactLabel}>Website</span>
                  <span className={`${styles.contactValue} ${!selectedCompany.website ? styles.contactValueMuted : ''}`}>
                    {selectedCompany.website ? (
                      <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer">
                        {selectedCompany.website}
                      </a>
                    ) : 'Not set'}
                  </span>
                </div>
                <div className={styles.contactDetailItem} style={{ marginTop: '0.75rem' }}>
                  <span className={styles.contactLabel}>Billing Address</span>
                  <span className={`${styles.contactValue} ${!selectedCompany.billing_address ? styles.contactValueMuted : ''}`}>{selectedCompany.billing_address || 'Not set'}</span>
                </div>
                <div className={styles.contactDetailItem} style={{ marginTop: '0.75rem' }}>
                  <span className={styles.contactLabel}>Notes</span>
                  <span className={`${styles.contactValue} ${!selectedCompany.company_notes ? styles.contactValueMuted : ''}`}>{selectedCompany.company_notes || 'No notes'}</span>
                </div>
              </div>

              {/* Main Contact Information */}
              <div className={styles.mainContactInfo}>
                <h3>Primary Contact</h3>
                {(() => {
                  const primaryContact = (selectedCompany.contacts || []).find(c => c.is_primary);
                  return primaryContact ? (
                    <>
                      <div className={styles.contactDetailItem}>
                        <span className={styles.contactLabel}>Name</span>
                        <span className={styles.contactValue}>{primaryContact.name || '—'}</span>
                      </div>
                      <div className={styles.contactDetailItem} style={{ marginTop: '0.75rem' }}>
                        <span className={styles.contactLabel}>Email</span>
                        <span className={`${styles.contactValue} ${!primaryContact.email ? styles.contactValueMuted : ''}`}>
                          {primaryContact.email ? <a href={`mailto:${primaryContact.email}`}>{primaryContact.email}</a> : 'Not set'}
                        </span>
                      </div>
                      <div className={styles.contactDetailItem} style={{ marginTop: '0.75rem' }}>
                        <span className={styles.contactLabel}>Phone</span>
                        <span className={`${styles.contactValue} ${!primaryContact.phone ? styles.contactValueMuted : ''}`}>{primaryContact.phone || 'Not set'}</span>
                      </div>
                      <div className={styles.contactDetailItem} style={{ marginTop: '0.75rem' }}>
                        <span className={styles.contactLabel}>Job Title</span>
                        <span className={`${styles.contactValue} ${!primaryContact.job_title ? styles.contactValueMuted : ''}`}>{primaryContact.job_title || 'Not set'}</span>
                      </div>
                      <div className={styles.contactDetailItem} style={{ marginTop: '0.75rem' }}>
                        <span className={styles.contactLabel}>Department</span>
                        <span className={`${styles.contactValue} ${!primaryContact.department ? styles.contactValueMuted : ''}`}>{primaryContact.department || 'Not set'}</span>
                      </div>
                    </>
                  ) : (
                    <span className={`${styles.contactValue} ${styles.contactValueMuted}`}>No primary contact assigned</span>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : selectedContact ? (
          <div key={selectedContact.user_id || selectedContact.client_contact_id} className={styles.contactDetailsCard}>
            <div className={styles.contactHero}>
              <div className={styles.contactAvatarContainer}>
                {selectedContact.avatar_url ? (
                  <img
                    src={selectedContact.avatar_url}
                    alt={selectedContact.name}
                    className={styles.contactAvatar}
                  />
                ) : (
                  <div className={styles.contactAvatarFallback}>
                    {selectedContact.name
                      .split(" ")
                      .map((word) => word[0])
                      .join("")
                      .slice(0, 2)}
                  </div>
                )}
              </div>
              <div className={styles.contactHeroText}>
                <h2 className={styles.contactName}>{selectedContact.name}</h2>
                <p className={styles.contactSubtitle}>
                  {selectedContact.job_title || 'No title'}
                  {selectedContact.department && ` · ${selectedContact.department}`}
                </p>
              </div>
              {((selectedContact.client_contact_id && canEditExternalContacts) ||
                (selectedContact.user_id && canEditPeople)) && (
                <div className={styles.contactActions}>
                  <button
                    type="button"
                    className={`${styles.addButton} ${styles.secondary}`}
                    onClick={() => setEditingContact(selectedContact)}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            <div className={styles.contactDetailsGrid}>
              <div className={styles.contactDetailItem}>
                <span className={styles.contactLabel}>Phone number</span>
                <span className={`${styles.contactValue} ${!selectedContact.phone ? styles.contactValueMuted : ''}`}>{selectedContact.phone || 'Not set'}</span>
              </div>
              <div className={styles.contactDetailItem}>
                <span className={styles.contactLabel}>Email</span>
                <span className={`${styles.contactValue} ${!selectedContact.email ? styles.contactValueMuted : ''}`}>
                  {selectedContact.email ? <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a> : 'Not set'}
                </span>
              </div>
              <div className={styles.contactDetailItem}>
                <span className={styles.contactLabel}>Job Title</span>
                <span className={`${styles.contactValue} ${!selectedContact.job_title ? styles.contactValueMuted : ''}`}>{selectedContact.job_title || 'Not set'}</span>
              </div>
              <div className={styles.contactDetailItem}>
                <span className={styles.contactLabel}>Department</span>
                <span className={`${styles.contactValue} ${!selectedContact.department ? styles.contactValueMuted : ''}`}>{selectedContact.department || 'Not set'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.emptyDetails}>
            <div className={styles.emptyDetailsIconWrap}>👤</div>
            <h3 className={styles.emptyDetailsTitle}>No Contact Selected</h3>
            <p className={styles.emptyDetailsText}>Select a company or contact from the list to view their details here.</p>
          </div>
        )}
        </div>
      </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddCompany && (
        <FormModal
          title="Add Company"
          onCancel={() => {
            setShowAddCompany(false);
            setCompanyForm({
              company_name: "",
              industry: "",
              website: "",
              billing_address: "",
              logo_url: "",
              company_notes: "",
              name: "",
              email: "",
              phone: "",
              job_title: "",
              department: "",
              is_primary: true,
              contact_notes: "",
            });
          }}
          onSubmit={handleAddCompany}
          overviewReadOnly={false}
          teamReadOnly={false}
          fields={[
            {
              label: "Company Name *",
              value: companyForm.company_name,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, company_name: e.target.value })),
            },
            {
              label: "Industry",
              value: companyForm.industry,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, industry: e.target.value })),
            },
            {
              label: "Website",
              value: companyForm.website,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, website: e.target.value })),
            },
            {
              label: "Company Logo",
              type: "file",
              accept: "image/*",
              onChange: (e) => setLogoFile(e.target.files[0]),
              note: "Upload a company logo (PNG, JPG, JPEG - max 2MB)",
            },
            {
              label: "Billing Address",
              type: "textarea",
              value: companyForm.billing_address,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, billing_address: e.target.value })),
            },
            {
              label: "Company Notes",
              type: "textarea",
              value: companyForm.company_notes,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, company_notes: e.target.value })),
            },
            { type: "divider", label: "Initial Contact (Optional)" },
            {
              label: "Name",
              value: companyForm.name,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, name: e.target.value })),
            },
            {
              label: "Email",
              type: "email",
              value: companyForm.email,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Phone",
              type: "tel",
              value: companyForm.phone,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, phone: e.target.value })),
            },
            {
              label: "Job Title",
              value: companyForm.job_title,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, job_title: e.target.value })),
            },
            {
              label: "Department",
              value: companyForm.department,
              onChange: (e) =>
                setCompanyForm((prev) => ({ ...prev, department: e.target.value })),
            },
          ]}
        />
      )}

      {/* Add Standalone Contact Modal */}
      {showAddStandaloneContact && (
        <FormModal
          title="Add Standalone Contact"
          onCancel={() => {
            setShowAddStandaloneContact(false);
            setStandaloneContactForm({
              name: "",
              email: "",
              phone: "",
              job_title: "",
              department: "",
              notes: "",
            });
          }}
          onSubmit={handleAddStandaloneContact}
          overviewReadOnly={false}
          teamReadOnly={false}
          fields={[
            {
              label: "Name",
              value: standaloneContactForm.name,
              onChange: (e) =>
                setStandaloneContactForm((prev) => ({ ...prev, name: e.target.value })),
            },
            {
              label: "Email",
              type: "email",
              value: standaloneContactForm.email,
              onChange: (e) =>
                setStandaloneContactForm((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Phone",
              type: "tel",
              value: standaloneContactForm.phone,
              onChange: (e) =>
                setStandaloneContactForm((prev) => ({ ...prev, phone: e.target.value })),
            },
            {
              label: "Job Title",
              value: standaloneContactForm.job_title,
              onChange: (e) =>
                setStandaloneContactForm((prev) => ({ ...prev, job_title: e.target.value })),
            },
            {
              label: "Department",
              value: standaloneContactForm.department,
              onChange: (e) =>
                setStandaloneContactForm((prev) => ({ ...prev, department: e.target.value })),
            },
            {
              label: "Notes",
              type: "textarea",
              value: standaloneContactForm.notes,
              onChange: (e) =>
                setStandaloneContactForm((prev) => ({ ...prev, notes: e.target.value })),
            },
          ]}
        />
      )}

      {/* Add Contact Modal */}
      {showAddContact && (
        <FormModal
          title="Add Contact"
          onCancel={() => {
            setShowAddContact(false);
            setSelectedCompanyId(null);
            setContactForm({
              name: "",
              email: "",
              phone: "",
              job_title: "",
              department: "",
              is_primary: false,
              notes: "",
            });
          }}
          onSubmit={handleAddContact}
          overviewReadOnly={false}
          teamReadOnly={false}
          fields={[
            {
              label: "Name",
              value: contactForm.name,
              onChange: (e) =>
                setContactForm((prev) => ({ ...prev, name: e.target.value })),
            },
            {
              label: "Email",
              type: "email",
              value: contactForm.email,
              onChange: (e) =>
                setContactForm((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Phone",
              type: "tel",
              value: contactForm.phone,
              onChange: (e) =>
                setContactForm((prev) => ({ ...prev, phone: e.target.value })),
            },
            {
              label: "Job Title",
              value: contactForm.job_title,
              onChange: (e) =>
                setContactForm((prev) => ({ ...prev, job_title: e.target.value })),
            },
            {
              label: "Department",
              value: contactForm.department,
              onChange: (e) =>
                setContactForm((prev) => ({ ...prev, department: e.target.value })),
            },
            {
              label: "Primary Contact",
              type: "checkbox",
              checked: contactForm.is_primary,
              onChange: (e) =>
                setContactForm((prev) => ({ ...prev, is_primary: e.target.checked })),
            },
            {
              label: "Notes",
              type: "textarea",
              value: contactForm.notes,
              onChange: (e) =>
                setContactForm((prev) => ({ ...prev, notes: e.target.value })),
            },
          ]}
        />
      )}

      {/* Edit Company Modal */}
      {editingCompany && canEditExternalContacts && (
        <FormModal
          title="Edit Company"
          onCancel={() => setEditingCompany(null)}
          onSubmit={handleUpdateCompany}
          overviewReadOnly={false}
          teamReadOnly={false}
          footerActions={
            <button
              type="button"
              className={ui.buttonDanger}
              onClick={handleDeleteCompany}
            >
              Delete Company
            </button>
          }
          fields={[
            {
              label: "Company Name *",
              value: editingCompany.company_name || "",
              onChange: (e) =>
                setEditingCompany((prev) => ({ ...prev, company_name: e.target.value })),
            },
            {
              label: "Industry",
              value: editingCompany.industry || "",
              onChange: (e) =>
                setEditingCompany((prev) => ({ ...prev, industry: e.target.value })),
            },
            {
              label: "Website",
              value: editingCompany.website || "",
              onChange: (e) =>
                setEditingCompany((prev) => ({ ...prev, website: e.target.value })),
            },
            {
              label: "Company Logo",
              type: "file",
              accept: "image/*",
              onChange: (e) => setLogoFile(e.target.files[0]),
              note: editingCompany.logo_url ? "Upload a new logo to replace the current one" : "Upload a company logo (PNG, JPG, JPEG - max 2MB)",
            },
            {
              label: "Billing Address",
              type: "textarea",
              value: editingCompany.billing_address || "",
              onChange: (e) =>
                setEditingCompany((prev) => ({ ...prev, billing_address: e.target.value })),
            },
            {
              label: "Notes",
              type: "textarea",
              value: editingCompany.company_notes || "",
              onChange: (e) =>
                setEditingCompany((prev) => ({ ...prev, company_notes: e.target.value })),
            },
          ]}
        />
      )}

      {/* Edit Contact Modal */}
      {editingContact && ((editingContact.client_contact_id && canEditExternalContacts) || (editingContact.user_id && canEditPeople)) && (
        <FormModal
          title="Edit Contact"
          onCancel={() => setEditingContact(null)}
          onSubmit={editingContact.client_contact_id ? handleUpdateContact : handleUpdateEmployee}
          overviewReadOnly={false}
          teamReadOnly={false}
          footerActions={
            editingContact.client_contact_id && canEditExternalContacts ? (
              <button
                type="button"
                className={ui.buttonDanger}
                onClick={handleDeleteContact}
              >
                Delete Contact
              </button>
            ) : null
          }
          fields={[
            {
              label: "Name",
              value: editingContact.name || "",
              onChange: (e) =>
                setEditingContact((prev) => ({ ...prev, name: e.target.value })),
            },
            {
              label: "Email",
              type: "email",
              value: editingContact.email || "",
              onChange: (e) =>
                setEditingContact((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Phone",
              type: "tel",
              value: editingContact.phone || "",
              onChange: (e) =>
                setEditingContact((prev) => ({ ...prev, phone: e.target.value })),
            },
            {
              label: "Job Title",
              value: editingContact.job_title || "",
              onChange: (e) =>
                setEditingContact((prev) => ({ ...prev, job_title: e.target.value })),
            },
            {
              label: "Department",
              value: editingContact.department || "",
              onChange: (e) =>
                setEditingContact((prev) => ({ ...prev, department: e.target.value })),
            },
            // Only show these fields for external contacts
            ...(editingContact.client_contact_id ? [
              {
                label: "Primary Contact",
                type: "checkbox",
                checked: editingContact.is_primary || false,
                onChange: (e) =>
                  setEditingContact((prev) => ({ ...prev, is_primary: e.target.checked })),
              },
              {
                label: "Notes",
                type: "textarea",
                value: editingContact.notes || "",
                onChange: (e) =>
                  setEditingContact((prev) => ({ ...prev, notes: e.target.value })),
              }
            ] : [])
          ]}
        />
      )}
    </div>
  );
}